import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import SortedTooltip from './SortedTooltip';
import ChartEmptyState from './ChartEmptyState';
import InfoTooltip from './InfoTooltip';

export interface LineSeries {
  key: string;
  color: string;
}

interface Props {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  lines: LineSeries[];
  height?: number;
  allowDecimals?: boolean;
  info?: string;
}

const SimpleLineChartCard = ({ title, data, xKey, lines, height = 220, allowDecimals = true, info }: Props) => {
  // Mirror the bar/pie primitives: an all-zero series renders the empty state
  // rather than a flat zero line, so the three primitives behave consistently.
  const hasValues = data?.some((row) => lines.some((line) => Number(row[line.key]) > 0));

  return (
  <div className="bg-white rounded-lg shadow p-4 w-full">
    <div className="flex items-center gap-1.5 mb-4">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      {info && <InfoTooltip text={info} />}
    </div>
    {!data?.length || !hasValues ? (
      <ChartEmptyState height={height} />
    ) : (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={allowDecimals} tick={{ fontSize: 12 }} />
          <Tooltip content={<SortedTooltip />} />
          <Legend />
          {lines.map((line) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              stroke={line.color}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    )}
  </div>
  );
};

export default SimpleLineChartCard;
